<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">智能行銷方案</h1>
      </div>
      <div class="header-actions">
        <button class="btn btn-secondary" @click="$router.push({ name: 'MarketingCampaignList' })">
                行銷活動列表
              </button>
      </div>
    </div>

    <div class="page-container">
    <p class="intro">
      依會員數據推薦可執行行銷方案，經表單精靈快速建立 Campaign 草稿，再於行銷活動流程編輯器細部調整。
    </p>
    <p class="intro-note">
      <i class="ri-information-line"></i>
      列表上的預估受眾人數僅供參考；實際人數請以建立活動並完成參與對象、流程步驟設定後，編輯頁「流程概覽」的試算結果為準。
    </p>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入推薦中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p>{{ error }}</p>
    </div>

    <div v-else class="recommendation-grid">
      <article v-for="item in items" :key="item.recommendationScenario" class="rec-card">
        <div class="rec-head">
          <h2>{{ item.recommendationName }}</h2>
        </div>
        <div class="rec-body">
          <p class="rec-reason">{{ item.reason }}</p>
          <p v-if="item.recommendationScenario === 'coupon_only'" class="rec-hint">
            適合活動期間自動觸發；若需立即整批發券請用
            <router-link :to="{ name: 'BatchCouponIssuances' }">批次發送優惠券</router-link>
          </p>
          <p v-if="item.recommendationScenario === 'register_gift'" class="rec-hint">
            適合有期間的行銷活動與成效追蹤；日常固定註冊禮請用
            <router-link :to="{ name: 'RegistrationGiftCampaignList' }">註冊禮設定</router-link>
            。兩邊同時啟用可能重複發券或推播。
          </p>
          <p v-if="item.recommendationScenario === 'questionnaire'" class="rec-hint">
            進入活動就用 LINE 派送問卷。受訪者填完後，才發送完成推播。請先發布問卷。
          </p>
          <p v-if="item.recommendationScenario === 'salon_booking'" class="rec-hint">
            美髮預約成立時發送確認推播，並附上一張優惠券。同一位顧客每次預約都會再觸發。預估受眾是近 30 天的預約筆數。
          </p>
          <p v-if="item.recommendationScenario === 'reservation_booked'" class="rec-hint">
            餐廳訂位成立時發送確認推播，並附上一張優惠券。同一位顧客每次訂位都會再觸發。預估受眾是近 30 天的訂位筆數。
          </p>
          <p v-if="item.recommendationScenario === 'dining_completed'" class="rec-hint">
            訂位改為已用餐完畢時發送感謝推播，並附上一張回訪優惠券。同一位顧客每次用餐完畢都會再觸發。預估受眾是近 30 天的用餐完畢筆數。
          </p>
          <p v-if="item.recommendationScenario === 'push_only'" class="rec-hint">
            適合「一次性或短檔期通知」情境，例如新品上架通知、限時活動提醒、門市快閃宣傳、節日單次關懷。
            若是每週／每月固定重複推播，建議改用
            <router-link :to="{ name: 'ClientLineBroadcastList' }">LINE 推播管理</router-link>
            的週期推播。
          </p>
          <ul class="rec-meta">
            <li>
              預估受眾：{{ item.estimatedAudience?.toLocaleString() }} 人
              <span class="rec-meta-note">（僅供參考）</span>
            </li>
            <li>預估成本：${{ formatMoney(item.estimatedCost) }}</li>
            <li>
              歷史 ROI：{{ item.historicalRoi != null ? formatRoi(item.historicalRoi) : '尚無資料' }}
            </li>
          </ul>
          <div v-if="item.riskFlags?.length" class="rec-risks">
            <p v-for="(risk, idx) in item.riskFlags" :key="idx">{{ risk.message }}</p>
          </div>
        </div>
        <button
          type="button"
          class="btn btn-primary rec-action"
          @click="goWizard(item.recommendationScenario)"
        >
          建立活動草稿
        </button>
      </article>
    </div>

    <p v-if="!loading && !error && items.length === 0" class="empty-state">
      目前沒有符合條件的推薦方案，請稍後再試或改從劇本手動建立。
    </p>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { smartMarketingService } from '@/api/modules/smartMarketing.js';
import { showError } from '@/utils/swal.js';

export default {
  name: 'SmartMarketingIndex',
  setup() {
    const router = useRouter();
    const loading = ref(true);
    const error = ref(null);
    const items = ref([]);

    const load = async () => {
      loading.value = true;
      error.value = null;
      try {
        const res = await smartMarketingService.getRecommendations();
        const data = res.data?.data ?? res.data;
        items.value = data?.items || [];
      } catch (e) {
        error.value = e.response?.data?.message || e.message || '載入失敗';
        showError(error.value);
      } finally {
        loading.value = false;
      }
    };

    const goWizard = (scenario) => {
      router.push({ name: 'SmartMarketingWizard', query: { scenario } });
    };

    const formatMoney = (value) => {
      const num = Number(value);
      if (!Number.isFinite(num)) return '-';
      return num.toLocaleString('zh-TW', { maximumFractionDigits: 0 });
    };

    const formatRoi = (value) => {
      const num = Number(value);
      if (!Number.isFinite(num)) return '-';
      return `${(num * 100).toFixed(1)}%`;
    };

    onMounted(load);

    return { loading, error, items, goWizard, formatMoney, formatRoi };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.intro {
  color: #6c757d;
  margin-bottom: 8px;
}

.intro-note {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 0 0 16px;
  padding: 10px 12px;
  font-size: 13px;
  line-height: 1.55;
  color: #495057;
  background: #f0f7ff;
  border: 1px solid #cfe2ff;
  border-radius: 8px;
}

.intro-note i {
  flex-shrink: 0;
  margin-top: 2px;
  font-size: 16px;
  color: #0d6efd;
}

.recommendation-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
  align-items: stretch;
}

.rec-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 280px;
}

.rec-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 0;
}

.rec-head {
  margin-bottom: 10px;
  flex-shrink: 0;
}

.rec-head h2 {
  margin: 0;
  font-size: 18px;
  line-height: 1.35;
}

.rec-reason {
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  min-height: 3em;
}

.rec-hint {
  margin: 0;
  padding: 8px 10px;
  font-size: 13px;
  line-height: 1.5;
  color: #495057;
  background: #f0f7ff;
  border: 1px solid #cfe2ff;
  border-radius: 6px;
}

.rec-hint a {
  color: #0d6efd;
  text-decoration: none;
  font-weight: 500;
}

.rec-hint a:hover {
  text-decoration: underline;
}

.rec-meta {
  margin: 0;
  padding-left: 18px;
  font-size: 13px;
  color: #495057;
}

.rec-meta li + li {
  margin-top: 4px;
}

.rec-meta-note {
  font-size: 12px;
  color: #9ca3af;
}

.rec-risks {
  font-size: 13px;
  color: #b45309;
  margin-top: auto;
}

.rec-risks p {
  margin: 0;
}

.rec-risks p + p {
  margin-top: 4px;
}

.rec-action {
  margin-top: 16px;
  width: 100%;
  flex-shrink: 0;
}

.empty-state {
  color: #6c757d;
}
</style>
